import { useQuery } from "@tanstack/react-query";
import { useGet, usePost } from "../../../common/api-service";
import { ProductSessionModel } from "./types";

/**
 * Bizbox Desk SSO — mints a product-AGNOSTIC session (facility roles across all products) via the
 * dedicated `/api/user/sso-desk` endpoint, then normalizes the response to the same {c0A1,c0B2} shape
 * the Desk callback consumes. Isolated from `useValidateProductSession` (which echoes an existing
 * product session): Desk gates on "has a registered facility", not on the Desk product. Runs once on
 * mount; a failure (no Beacon session) surfaces as `error` → the page bounces the caller to login.
 */
export const useValidateDeskSession = () => {
  const { post } = usePost();

  return useQuery<ProductSessionModel>({
    queryFn: async () => {
      const res = await post<
        { productCode: string },
        { accessToken: string; refreshToken: string }
      >("/api/user/sso-desk", { productCode: "" }, false);
      return { c0A1: res.accessToken, c0B2: res.refreshToken };
    },
    queryKey: [`desk-session-model`],
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 0,
  });
};

export const useValidateProductSession = () => {
  const { get } = useGet();

  return useQuery<ProductSessionModel>({
    queryFn: () =>
      get<ProductSessionModel>(
        "/api/auth/validate-product-session",
        undefined,
        false
      ),
    queryKey: [`product-session-model`],
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 0,
  });
};

export const useValidateUserSession = () => {
  const { get } = useGet();

  return useQuery<boolean>({
    queryFn: () =>
      get<boolean>("/api/auth/validate-user-session", undefined, false),
    queryKey: [`user-session`],
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 0,
  });
};
